import {
  ScopeMode,
  ServiceStartDayOperator,
  ServiceStartDayValue,
  ServiceStartTimeOperator,
} from '@billing/validation/rules';
import { Group, Select, TextInput } from '@mantine/core';
import type { ReactElement } from 'react';
import { getWhenComparisonValue, getWhenComparisonUpdate } from './ruleUtils';
import type { RuleCriteriaClauseProps } from './RuleCriteriaRow';

const RuleServiceTimeCriteriaFields = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement => {
  const { translate, updateWhenClause, localizedWhenOperatorOptions } = builder;
  return (
    <>
      <Select
        label={translate('pages:rules.editControls.operator')}
        data={[
          {
            value: ServiceStartTimeOperator.Equal,
            label: translate('pages:rules.editControls.equal'),
          },
          {
            value: ServiceStartTimeOperator.BetweenInclusive,
            label: translate('pages:rules.editControls.between'),
          },
          {
            value: ServiceStartTimeOperator.NotBetweenInclusive,
            label: translate('pages:rules.editControls.notBetween'),
          },
          {
            value: 'notEqual',
            label: translate('pages:rules.criteriaComparisons.notEqual'),
          },
          ...localizedWhenOperatorOptions.filter(
            (option) =>
              option.value === 'exists' || option.value === 'notExists'
          ),
        ]}
        value={getWhenComparisonValue(clause)}
        onChange={(value) =>
          updateWhenClause(clause.id, {
            ...getWhenComparisonUpdate(
              clause.scopeMode,
              value || ServiceStartTimeOperator.Equal
            ),
          })
        }
        allowDeselect={false}
        data-testid={
          index === 0
            ? 'rule-service-start-operator'
            : `rule-service-start-operator-${index}`
        }
      />
      {(clause.whenOperator === 'is' || clause.whenOperator === 'isNot') &&
        (clause.serviceStartTimeOperator === ServiceStartTimeOperator.Equal ? (
          <TextInput
            type="time"
            label={translate('pages:rules.editControls.serviceStartTime')}
            description={translate('pages:rules.editControls.siteTimezone')}
            value={clause.serviceStartTimeValue}
            onChange={(event) =>
              updateWhenClause(clause.id, {
                serviceStartTimeValue: event.currentTarget.value,
              })
            }
            data-testid={
              index === 0
                ? 'rule-service-start-time'
                : `rule-service-start-time-${index}`
            }
          />
        ) : (
          <Group grow>
            <TextInput
              type="time"
              label={translate('pages:rules.editControls.startTime')}
              description={translate('pages:rules.editControls.siteTimezone')}
              value={clause.serviceStartTimeStart}
              onChange={(event) =>
                updateWhenClause(clause.id, {
                  serviceStartTimeStart: event.currentTarget.value,
                })
              }
              data-testid={
                index === 0
                  ? 'rule-service-start-time-range-start'
                  : `rule-service-start-time-range-start-${index}`
              }
            />
            <TextInput
              type="time"
              label={translate('pages:rules.editControls.endTime')}
              description={translate('pages:rules.editControls.siteTimezone')}
              value={clause.serviceStartTimeEnd}
              onChange={(event) =>
                updateWhenClause(clause.id, {
                  serviceStartTimeEnd: event.currentTarget.value,
                })
              }
              data-testid={
                index === 0
                  ? 'rule-service-start-time-range-end'
                  : `rule-service-start-time-range-end-${index}`
              }
            />
          </Group>
        ))}
    </>
  );
};

const RuleServiceDayCriteriaFields = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement => {
  const {
    translate,
    updateWhenClause,
    localizedWhenOperatorOptions,
    localizedDayOptions,
  } = builder;
  return (
    <>
      <Select
        label={translate('pages:rules.editControls.operator')}
        data={[
          {
            value: ServiceStartDayOperator.Equal,
            label: translate('pages:rules.editControls.equal'),
          },
          {
            value: ServiceStartDayOperator.BetweenInclusive,
            label: translate('pages:rules.editControls.between'),
          },
          {
            value: ServiceStartDayOperator.NotBetweenInclusive,
            label: translate('pages:rules.editControls.notBetween'),
          },
          {
            value: 'notEqual',
            label: translate('pages:rules.criteriaComparisons.notEqual'),
          },
          ...localizedWhenOperatorOptions.filter(
            (option) =>
              option.value === 'exists' || option.value === 'notExists'
          ),
        ]}
        value={getWhenComparisonValue(clause)}
        onChange={(value) =>
          updateWhenClause(clause.id, {
            ...getWhenComparisonUpdate(
              clause.scopeMode,
              value || ServiceStartDayOperator.Equal
            ),
          })
        }
        allowDeselect={false}
        data-testid={
          index === 0
            ? 'rule-service-start-day-operator'
            : `rule-service-start-day-operator-${index}`
        }
      />
      {(clause.whenOperator === 'is' || clause.whenOperator === 'isNot') &&
        (clause.serviceStartDayOperator === ServiceStartDayOperator.Equal ? (
          <Select
            label={translate('pages:rules.editControls.serviceDay')}
            description={translate('pages:rules.editControls.siteTimezone')}
            data={localizedDayOptions}
            value={clause.serviceStartDayValue}
            onChange={(value) =>
              updateWhenClause(clause.id, {
                serviceStartDayValue:
                  (value as ServiceStartDayValue) ||
                  ServiceStartDayValue.Sunday,
              })
            }
            allowDeselect={false}
            data-testid={
              index === 0
                ? 'rule-service-start-day-match'
                : `rule-service-start-day-match-${index}`
            }
          />
        ) : (
          <Group grow>
            <Select
              label={translate('pages:rules.editControls.startDay')}
              description={translate('pages:rules.editControls.siteTimezone')}
              data={localizedDayOptions}
              value={clause.serviceStartDayStart}
              onChange={(value) =>
                updateWhenClause(clause.id, {
                  serviceStartDayStart:
                    (value as ServiceStartDayValue) ||
                    ServiceStartDayValue.Monday,
                })
              }
              allowDeselect={false}
              data-testid={
                index === 0
                  ? 'rule-service-start-day-range-start'
                  : `rule-service-start-day-range-start-${index}`
              }
            />
            <Select
              label={translate('pages:rules.editControls.endDay')}
              description={translate('pages:rules.editControls.siteTimezone')}
              data={localizedDayOptions}
              value={clause.serviceStartDayEnd}
              onChange={(value) =>
                updateWhenClause(clause.id, {
                  serviceStartDayEnd:
                    (value as ServiceStartDayValue) ||
                    ServiceStartDayValue.Friday,
                })
              }
              allowDeselect={false}
              data-testid={
                index === 0
                  ? 'rule-service-start-day-range-end'
                  : `rule-service-start-day-range-end-${index}`
              }
            />
          </Group>
        ))}
    </>
  );
};

/** Renders site-local time or weekday controls below the row's field selector. */
export const RuleServiceCriteriaFields = (
  props: RuleCriteriaClauseProps
): ReactElement | null => {
  if (props.clause.scopeMode === ScopeMode.ServiceStartTimeLocal)
    return <RuleServiceTimeCriteriaFields {...props} />;
  if (props.clause.scopeMode === ScopeMode.ServiceStartDayLocal)
    return <RuleServiceDayCriteriaFields {...props} />;
  return null;
};
